import { supabase } from '../lib/supabaseClient';

// ─── Types ──────────────────────────────────────────────────────

export interface EntryFlow {
  id: string;
  name: string;
  category: string | null;
  entryLabel: string;
  entryDescription: string | null;
  entryIcon: string | null;
  entryRoles: string[];
  versionSemver: string;
  latestVersionId: string | null;
  usageCount: number;
  lastExecutedAt: string | null;
  createdAt: string | null;
}

// ─── Service ────────────────────────────────────────────────────

export class EntryFlowService {
  /**
   * List blueprints marked as entry points.
   * Optionally filter by user's workflow role key.
   */
  static async getEntryFlows(organizationId: string, roleKey?: string): Promise<EntryFlow[]> {
    if (!organizationId) throw new Error('organizationId é obrigatório');

    const baseSelect = 'id, name, category, entry_label, entry_description, entry_icon, entry_roles, version_semver, latest_version_id, created_at';

    // 1) Prefer blueprints explicitly marked as entry points
    const { data, error } = await supabase
      .from('process_blueprints_with_latest_version')
      .select(baseSelect)
      .eq('organization_id', organizationId)
      .eq('is_entry_point', true)
      .in('status', ['published', 'standard'])
      .order('entry_label', { ascending: true });

    if (error) {
      console.warn('[EntryFlowService.getEntryFlows]', error.message);
      throw error;
    }

    let rows = data ?? [];

    // 2) Fallback: nenhum entry-point configurado → mostra todos os blueprints
    //    publicados/padrão para que o LauncherDrawer nunca fique vazio.
    if (rows.length === 0) {
      const fallback = await supabase
        .from('process_blueprints_with_latest_version')
        .select(baseSelect)
        .eq('organization_id', organizationId)
        .in('status', ['published', 'standard'])
        .order('name', { ascending: true });
      if (fallback.error) {
        console.warn('[EntryFlowService.getEntryFlows][fallback]', fallback.error.message);
      } else {
        rows = fallback.data ?? [];
        if (rows.length > 0) {
          console.info('[EntryFlowService] Fallback ativado — exibindo blueprints publicados (nenhum is_entry_point=true).');
        }
      }
    }

    // Fetch usage counts in parallel
    const ids = rows.map((r: any) => r.id);
    let usageMap = new Map<string, number>();
    let lastExecMap = new Map<string, string>();
    if (ids.length > 0) {
      const { data: usageData } = await supabase
        .from('process_blueprint_usage_count')
        .select('blueprint_id, usage_count')
        .eq('organization_id', organizationId)
        .in('blueprint_id', ids);
      if (usageData) {
        for (const u of usageData) {
          usageMap.set(u.blueprint_id, u.usage_count ?? 0);
        }
      }

      // Fetch last execution per blueprint via templates
      const { data: tplRows } = await supabase
        .from('workflow_templates_v9')
        .select('id, blueprint_id')
        .eq('organization_id', organizationId)
        .in('blueprint_id', ids);
      if (tplRows && tplRows.length > 0) {
        const tplToBp = new Map<string, string>();
        for (const t of tplRows) tplToBp.set(t.id, t.blueprint_id);
        const tplIds = tplRows.map(t => t.id);
        const { data: execRows } = await supabase
          .from('workflow_instances_v9')
          .select('template_id, started_at')
          .eq('organization_id', organizationId)
          .in('template_id', tplIds)
          .order('started_at', { ascending: false });
        if (execRows) {
          for (const e of execRows) {
            const bpId = tplToBp.get(e.template_id);
            if (bpId && !lastExecMap.has(bpId)) {
              lastExecMap.set(bpId, e.started_at);
            }
          }
        }
      }
    }

    // Client-side role filter (entry_roles is TEXT[] — PostgREST array contains is limited)
    if (roleKey) {
      rows = rows.filter((r: any) => {
        const roles: string[] = r.entry_roles ?? [];
        return roles.length === 0 || roles.includes(roleKey);
      });
    }

    return rows.map((r: any) => ({
      id: r.id,
      name: r.name,
      category: r.category ?? null,
      entryLabel: r.entry_label || r.name,
      entryDescription: r.entry_description ?? null,
      entryIcon: r.entry_icon ?? null,
      entryRoles: r.entry_roles ?? [],
      versionSemver: r.version_semver ?? '1.0.0',
      latestVersionId: r.latest_version_id ?? null,
      usageCount: usageMap.get(r.id) ?? 0,
      lastExecutedAt: lastExecMap.get(r.id) ?? null,
      createdAt: r.created_at ?? null,
    }));
  }

  /**
   * Mark/unmark a blueprint as an entry point (admin action).
   */
  static async toggleEntryPoint(
    blueprintId: string,
    config: {
      isEntryPoint: boolean;
      entryLabel?: string;
      entryDescription?: string;
      entryIcon?: string;
      entryRoles?: string[];
    },
  ): Promise<void> {
    const { error } = await supabase
      .from('process_blueprints')
      .update({
        is_entry_point: config.isEntryPoint,
        entry_label: config.entryLabel ?? null,
        entry_description: config.entryDescription ?? null,
        entry_icon: config.entryIcon ?? null,
        entry_roles: config.entryRoles ?? [],
      })
      .eq('id', blueprintId);

    if (error) throw error;
  }
}
